<template>
  <div class="wage-calculator-container">
    <SalaryCalculator
      v-if="!calcPanel"
      :bg-img="
        isMobile()
          ? 'https://video.reta-inc.com/image/default/1242424075664D888CE01C6F48CCDB76-6-2.jpg'
          : 'https://video.reta-inc.com/image/default/D6E23E8BDB0945E5924AC94A42AF72AC-6-2.jpg'
      "
      title="员工工资计算器"
      desc="帮助员工了解税后工资，方便您选择有竞争力的全球性职位"
    >
      <Calc tip="员工实得工资为预估值，且会受不同管辖区的法律政策影响。仅供参考" />
    </SalaryCalculator>
    <div class="main" v-else>
      <div>
        <SalaryCalculator
          class="mobile_computer"
          :bg-img="
            isMobile()
              ? 'https://video.reta-inc.com/image/default/1242424075664D888CE01C6F48CCDB76-6-2.jpg'
              : 'https://video.reta-inc.com/image/default/D6E23E8BDB0945E5924AC94A42AF72AC-6-2.jpg'
          "
          title="员工工资计算器"
          desc="帮助员工了解税后工资，方便您选择有竞争力的全球性职位"
        >
          <Calc tip="员工实得工资为预估值，且会受不同管辖区的法律政策影响。仅供参考" />
        </SalaryCalculator>
        <SalaryCalculatorDetail
          :countryCode="String(query.countryCode)"
          :amount="Number(query.amount)"
          title="员工工资计算器"
          class="computer"
        />
        <div class="res-container" ref="resContainer">
          <div class="nav">
            <a-radio-group type="button" v-model="calcTime">
              <a-radio value="mouth">月</a-radio>
              <a-radio value="year">年</a-radio>
            </a-radio-group>
          </div>
          <div class="res-panel">
            <div class="panel">
              <div class="title">{{ calcPanel.name }}</div>
              <div class="content">
                <template v-for="(item, j) in calcPanel.list" :key="j">
                  <div v-if="!item.idDetail" class="item">
                    <div class="label">{{ item.label }}</div>
                    <div class="money">{{ item.value }}</div>
                  </div>
                  <div v-else class="detail-info">
                    <div
                      class="detail-item"
                      v-for="(salaryCalcVo, k) in item.salaryCalcVoList"
                      :key="k"
                      :class="{ all: k === item.salaryCalcVoList.length - 1 }"
                    >
                      <div class="label">{{ salaryCalcVo.ruleName }}</div>
                      <div class="money">USD {{ salaryCalcVo.needPayDollars }}</div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="content-right">
        <EmploymentTools />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ICalculatorCalcNewCountryRes, ICalculatorCalcRes } from '~~/common/web-api/types';
import { countryList } from '@/common/global';
import { isMobile } from '@/common/utils';

const route = useRoute();

const query = route.query;
const calcTime = ref('year');

const calcResult = ref<ICalculatorCalcRes | ICalculatorCalcNewCountryRes | null>(null);
if (query.amount && query.countryCode) {
  const { data } = await useAsyncData(async () => {
    const { data } = await useFetch<{ data: string }>('https://e.dghire.com/api/a/website/public/v1/calculator', {
      method: 'POST',
      body: {
        amount: Number(query.amount),
        countryCode: query.countryCode,
      },
    });
    return { data: data.value };
  });
  if (data.value?.data) {
    if (countryList.find(c => c.name === query.countryCode)) {
      calcResult.value = JSON.parse(data.value.data.data).data as ICalculatorCalcRes;
    } else {
      calcResult.value = JSON.parse(data.value?.data?.data) as ICalculatorCalcNewCountryRes;
    }
  }
}

const currentCountry = computed(() => {
  return countryList.find(c => c.name === calcResult.value?.countryCode)?.desc;
});

useSeoHead({
  title: `${currentCountry.value ?? ''}`,
});

const calcPanel = computed(() => {
  if (!calcResult.value) {
    return null;
  }
  const isYear = calcTime.value === 'year';
  const isYearLabel = isYear ? '年度' : '月度';
  if (countryList.find(c => c.name === query.countryCode)) {
    const { employeeCostForYear, employeeCostForMonth } = calcResult.value as ICalculatorCalcRes;
    return {
      name: `${currentCountry.value || ''}员工工资`,
      list: [
        {
          label: `${isYearLabel}薪资`,
          value: `USD ${
            isYear ? Number(Number(query.amount).toFixed(2)) : Number((Number(query.amount) / 12).toFixed(2))
          }`,
        },
        {
          label: '个税及社会保障',
          value: '',
        },
        {
          idDetail: true,
          salaryCalcVoList: (isYear
            ? employeeCostForYear.salaryCalcVoList
            : employeeCostForMonth.salaryCalcVoList
          )?.concat({
            needPayDollars: isYear ? employeeCostForYear.itemTotalFeeDollars : employeeCostForMonth.itemTotalFeeDollars,
            ruleName: '总计',
          }),
        },
        {
          label: `${isYearLabel}净工资`,
          value: `USD ${
            isYear
              ? Number((Number(query.amount) - employeeCostForYear.itemTotalFeeDollars).toFixed(2))
              : Number((Number(query.amount) / 12 - employeeCostForMonth.itemTotalFeeDollars).toFixed(2))
          }`,
        },
      ],
    };
  }
});
</script>

<style lang="less" scoped>
@media screen and (min-width: 800px) {
  .wage-calculator-container {
    padding-bottom: 80px;
    background-color: #f5f8ff;
    .main {
      display: flex;
      justify-content: center;
      .mobile_computer {
        display: none;
      }

      .content-right {
        height: 500px;
        margin-top: 180px;
        margin-left: 24px;
      }

      .res-container {
        position: relative;
        margin: 12px auto 0px;
        padding-bottom: 80px;

        .nav {
          width: 680px;
          margin: 0px auto;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .country {
            font-weight: 500;
            font-size: 24px;
            line-height: 34px;
            color: #1d2129;
          }

          :deep(.arco-radio-group-button) {
            border: 1px solid rgba(22, 93, 255, 0.5);
            border-radius: 4px;
            height: 32px;
            padding: 4px;
            box-sizing: border-box;
            background-color: transparent;

            .arco-radio-button {
              margin: 0;
              border-radius: 4px;

              &.arco-radio-checked {
                background: #165dff;

                .arco-radio-button-content {
                  color: #fff;
                  font-weight: 500;
                }
              }

              .arco-radio-button-content {
                font-weight: 400;
                font-size: 12px;
                line-height: 22px;
                color: #165dff;
              }
            }
          }
        }

        .res-panel {
          width: 680px;
          min-height: 550px;
          margin: 0px auto;
          display: flex;
          justify-content: space-between;
          margin-top: 12px;

          .panel {
            width: 100%;
            background: #ffffff;
            border-radius: 12px;
            padding: 24px 40px;
            box-sizing: border-box;
            flex-shrink: 0;
            padding-bottom: 150px;

            .title {
              font-weight: 500;
              font-size: 20px;
              line-height: 28px;
              color: #000000;
              padding-bottom: 24px;
              border-bottom: 1px solid #e3e8f2;
            }

            .content {
              display: flex;
              flex-direction: column;
              padding-top: 8px;

              .item {
                margin-top: 24px;
                display: flex;
                justify-content: space-between;
                align-items: center;

                .label {
                  font-weight: 400;
                  font-size: 20px;
                  line-height: 28px;
                  color: #4e5969;
                }

                .money {
                  font-weight: 700;
                  font-size: 20px;
                  line-height: 23px;
                  color: #1d2129;
                }
              }

              .detail-info {
                width: 100%;
                padding: 20px;
                box-sizing: border-box;
                background: #f5f8ff;
                border-radius: 8px;
                margin-top: 20px;

                .detail-item {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-top: 16px;

                  &:first-child {
                    margin-top: 0;
                  }

                  .label {
                    font-weight: 400;
                    font-size: 16px;
                    line-height: 22px;
                    color: #4e5969;
                  }

                  .money {
                    font-weight: 700;
                    font-size: 16px;
                    line-height: 19px;
                    color: #4e5969;
                  }

                  &.all {
                    .label {
                      font-weight: 500;
                      font-size: 20px;
                      line-height: 28px;
                      color: #1d2129;
                    }

                    .money {
                      font-weight: 700;
                      font-size: 20px;
                      line-height: 23px;
                      color: #1d2129;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
<style lang="less" scoped>
@media screen and (max-width: 800px) {
  .wage-calculator-container {
    width: 100%;
    padding-bottom: 40px;
    background-color: #f5f8ff;

    .main {
      .computer {
        display: none;
      }
      .content-right {
        display: none;
      }

      .res-container {
        padding: 0 16px;
        margin-top: 26px;

        .nav {
          width: 100%;
          margin: 0px auto;
          display: flex;
          justify-content: space-between;
          align-items: center;

          .country {
            font-weight: 500;
            font-size: 20px;
            line-height: 28px;
            color: #1d2129;
          }

          :deep(.arco-radio-group-button) {
            border: 1px solid rgba(22, 93, 255, 0.5);
            border-radius: 4px;
            height: 32px;
            padding: 4px;
            box-sizing: border-box;
            background-color: transparent;

            .arco-radio-button {
              margin: 0;
              border-radius: 4px;

              &.arco-radio-checked {
                background: #165dff;

                .arco-radio-button-content {
                  color: #fff;
                  font-weight: 500;
                }
              }

              .arco-radio-button-content {
                font-weight: 400;
                font-size: 12px;
                line-height: 22px;
                color: #165dff;
              }
            }
          }
        }

        .res-panel {
          width: 100%;
          min-height: 550px;
          margin-top: 18px;
          display: flex;
          justify-content: space-between;
          margin-top: 12px;

          .panel {
            width: 100%;
            background: #ffffff;
            border-radius: 12px;
            padding: 20px 16px;
            box-sizing: border-box;
            flex-shrink: 0;
            // padding-bottom: 150px;

            .title {
              font-weight: 500;
              font-size: 20px;
              line-height: 28px;
              color: #000000;
              padding-bottom: 16px;
              border-bottom: 1px solid #e3e8f2;
            }

            .content {
              display: flex;
              flex-direction: column;
              padding-top: 4px;

              .item {
                margin-top: 20px;
                display: flex;
                justify-content: space-between;
                align-items: center;

                .label {
                  font-weight: 400;
                  font-size: 14px;
                  line-height: 20px;
                  color: #4e5969;
                }

                .money {
                  font-weight: 700;
                  font-size: 14px;
                  line-height: 16px;
                  color: #1d2129;
                }
              }

              .detail-info {
                width: 100%;
                padding: 16px;
                box-sizing: border-box;
                background: #f5f8ff;
                border-radius: 8px;
                margin-top: 20px;

                .detail-item {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-top: 16px;

                  &:first-child {
                    margin-top: 0;
                  }

                  .label {
                    font-weight: 400;
                    font-size: 12px;
                    line-height: 17px;
                    color: #4e5969;
                  }

                  .money {
                    font-weight: 700;
                    font-size: 12px;
                    line-height: 14px;
                    color: #4e5969;
                  }

                  &.all {
                    .label {
                      font-weight: 500;
                      font-size: 14px;
                      line-height: 20px;
                      color: #1d2129;
                    }

                    .money {
                      font-weight: 700;
                      font-size: 14px;
                      line-height: 16px;
                      color: #1d2129;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
